import { createAction } from '@reduxjs/toolkit';
import * as itemTypes from '../../items/item-types';
import {
  getItem as getItemInternal,
  saveItem as saveItemInternal
} from '../../items/item.actions';
import { getCapValidationMessage } from '../campaign-utils.js';
import { validateEpcOverride } from '../epc-override';
import { TEMPORARY_SUPPRESSION } from './campaign-info.jsx';
import { DEFAULT_END_DATE_STRING } from '../campaign-utils.js';

export const receiveCampaign = createAction('receiveCampaign');
export const setCampaignIsBusy = createAction('setCampaignIsBusy');
export const modifyCampaign = createAction('modifyCampaign');
export const closeCampaign = createAction('closeCampaign');

const campaignModel = (id, name, params) => ({
  id,
  name,
  type: itemTypes.CAMPAIGN,
  flowIds: [],
  capStrategy: 'none',
  lookBackWindowInDays: 7,
  suppressionType: 'permanent',
  campaignCPAEventId: null
});

export const getItem = getItemInternal(
  campaignModel,
  receiveCampaign,
  setCampaignIsBusy
);

// The onBeforeSave transform for a live Campaign save's PUT: strips the
// EPC Override checkbox (a UI-only toggle) and the ignored adGroups
// collection. Exported so schedule-mode-form.jsx can run the exact same
// transform before calculating/posting a scheduled change - unlike a live
// save's PUT, which tolerates an extra property it doesn't recognize, the
// schedule endpoint rejects (or, in a mixed change, silently drops) one it
// doesn't.
export const transformCampaignForSave = itemCopy => {
  if (itemCopy.epcOverrideChecked === false) {
    itemCopy.epcOverrideExpiry = DEFAULT_END_DATE_STRING;
  }
  delete itemCopy.epcOverrideChecked;
  // delete adGroup collection since it's ignored anyways on campaign save
  delete itemCopy.adGroups;
};

export const saveItem = saveItemInternal(
  modifyCampaign,
  item => {
    const overrideError = validateEpcOverride(item);
    const converMappingErrors = conversionMappingValidation(
      item.conversionMappings
    );

    if (overrideError) return overrideError;
    if (converMappingErrors) return converMappingErrors;

    if (!item.advertiserId) {
      return 'Advertiser is required.';
    }

    if (!item.goal) {
      return 'Campaign Goal is required.';
    }

    if (
      !item.campaignType ||
      item.campaignType === 'wall' ||
      item.campaignType === 'inflow'
    ) {
      return 'Campaign Type is required.';
    }

    if (!item.lookBackWindowInDays) {
      return 'Conversion Lookback is required.';
    }

    if (item.lookBackWindowInDays < 1 || item.lookBackWindowInDays > 31) {
      return 'Conversion Lookback value must be between 1 and 31 days.';
    }

    if (!item.verticalId) {
      return 'Vertical is required.';
    }

    if (!item.subVerticalId) {
      return 'Subvertical is required.';
    }

    if (item?.suppressionType === TEMPORARY_SUPPRESSION) {
      if (
        item.repeatConversionAfterDays < 1 ||
        item.repeatConversionAfterDays > 365
      ) {
        return 'Allow Repeat Conversions must be between 1 and 365 days.';
      }
    }

    if (item.campaignCPAGoalAmount && !item.campaignCPAGoalType) {
      return 'Campaign CPA Goal Type is required.';
    }

    if (
      item.automatedBudgetOptimization &&
      (item.capStrategy !== 'revenue' ||
        !item.dailyRevenueCap ||
        item.dailyRevenueCap <= 0)
    ) {
      return 'Automated Budget Optimization requires a Revenue cap strategy with a daily budget cap set.';
    }

    return getCapValidationMessage({
      dailyCap: item.dailyConversionCap,
      monthlyCap: item.monthlyConversionCap,
      overallCap: item.overallConversionCap
    });
  },
  transformCampaignForSave
);

// Validate conversion mapping
const conversionMappingValidation = conversionMappingValidation => {
  if (conversionMappingValidation && conversionMappingValidation.length > 0) {
    let errorMessage = '';
    conversionMappingValidation.forEach(cm => {
      if (!cm.tuneOfferId) {
        errorMessage = 'Tune Offer ID is required';
        return;
      }
      if (!cm.conversionTypeIntegrationId) {
        errorMessage = 'Event ID (Goal ID) is required';
      }
      if (!cm.displayName) {
        errorMessage = 'Event Name is required';
      }
      if (cm.eventValueType === 'fixed' && !cm.eventValue) {
        errorMessage = 'Event Value is required when Event Value Type is Fixed';
      }
    });

    return errorMessage;
  }
};
